import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import { ProjectActivity } from '../../types/ActivityTypes';
import { getProjectActivities } from '../../api/activityApi';
import { getProgressConsolidatedPerformance } from '../../api/consolidatedPerformanceApi';
import { Tower } from '../../types/towerTypes';
import { getTowersByProjectId } from '../../api/towerApi';
import ExcelJS from "exceljs";
import logo from '../../assets/images/logoElecnor.png';
import { saveAs } from "file-saver";

interface PerformanceTableProps {
    selectedProject?: number;
}

const ProductionVerification: React.FC<PerformanceTableProps> = ({ selectedProject }) => {

    const [activitiesProject, setActivitiesProject] = useState<ProjectActivity[]>([]);
    const [progressTowerData, setProgressTowerData] = useState<{ idProject: number, nameProject: string, towers: any[] }>({
        idProject: 0,
        nameProject: "",
        towers: []
    });
    const [towersList, setTowerData] = useState<Tower[]>([]);

    useEffect(() => {
        if (selectedProject) {
            fetchActivitiesData();
        }
    }, [selectedProject]);

    const exportTableToExcel = async () => {
        const table = document.getElementById("tabla-produccion");
        if (!table) {
            alert("No se encontró la tabla para exportar.");
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Verificación");
        const rows = table.querySelectorAll("tr");
        const columnWidths: number[] = [];

        // Carga de imagen
        const imageResponse = await fetch(logo);
        const imageBuffer = await imageResponse.arrayBuffer();
        const imageId = workbook.addImage({
            buffer: imageBuffer,
            extension: 'png',
        });
        worksheet.addImage(imageId, {
            tl: { col: 0, row: 0 },
            ext: { width: 160, height: 60 },
        });

        // Nombre del proyecto
        if (activitiesProject.length > 0 && activitiesProject[0].project?.name) {
            const projectName = activitiesProject[0].project.name;
            const projectCell = worksheet.getCell('D2');
            projectCell.value = `PROYECTO: ${projectName.toUpperCase() || ''}`;
            projectCell.font = { bold: true, size: 18 };
            projectCell.alignment = { horizontal: 'left', vertical: 'middle' };
        }

        let rowIndex = 5;
        rows.forEach((row, index) => {
            const excelRow = worksheet.getRow(rowIndex + 1);
            const cells = row.querySelectorAll("td, th");

            cells.forEach((cell, cellIndex) => {
                if (cell instanceof HTMLElement) {
                    let value = cell.innerText.trim();

                    // CORRECCIÓN 1: Declaración explícita de la variable para evitar errores TS
                    let cellRefValue: string | number = value;
                    let numericValue = parseFloat(value.replace("%", "").replace(",", "."));

                    if (!isNaN(numericValue)) {
                        // Si viene con %, asumimos que es el valor entero (ej: 50%) -> 0.5 para excel
                        if (value.includes("%")) {
                           cellRefValue = numericValue / 100; // Excel prefiere 0.5 para formato %
                        } else {
                           cellRefValue = numericValue;
                        }
                    }

                    const cellRef = excelRow.getCell(cellIndex + 1);
                    cellRef.value = cellRefValue;

                    // Formato Excel
                    if (typeof cellRefValue === 'number' && value.includes("%")) {
                        cellRef.numFmt = '0%'; // Formato entero sin decimales en Excel

                        // Pintar amarillo si > 0 y < 1 (es decir, < 100%)
                        if (cellRefValue > 0 && cellRefValue < 1) {
                            cellRef.fill = {
                                type: "pattern",
                                pattern: "solid",
                                fgColor: { argb: "FFFF00" } // Amarillo estándar
                            };
                        }
                    }

                    // Ajuste de anchos (lógica visual)
                    const currentColumnWidth = value.length;
                    if (columnWidths[cellIndex] == null || columnWidths[cellIndex] < currentColumnWidth) {
                        columnWidths[cellIndex] = currentColumnWidth;
                    }

                    cellRef.border = {
                        top: { style: "thin" },
                        left: { style: "thin" },
                        bottom: { style: "thin" },
                        right: { style: "thin" },
                    };
                }
            });
            excelRow.commit();
            rowIndex++;
        });

        columnWidths.forEach((width, index) => {
            worksheet.getColumn(index + 1).width = width + 5;
        });

        workbook.xlsx.writeBuffer().then((buffer) => {
            const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
            saveAs(blob, "verificacion_produccion.xlsx");
        });
    };

    const fetchActivitiesData = async () => {
        try {
            const columnsActivities = await getProjectActivities(selectedProject || 0);
            setActivitiesProject(columnsActivities);

            const fetchedTowers = await getTowersByProjectId(selectedProject || 0);
            setTowerData(fetchedTowers);

            const fetchProgressTower = await getProgressConsolidatedPerformance(selectedProject || 0);
            setProgressTowerData(fetchProgressTower);

        } catch (error) {
            console.error("Hubo un problema" + error);
        }
    };

    /**
     * CORRECCIÓN 2: Lógica de obtención de progreso
     * - Busca por ID (activityId) en vez de nombre para mayor precisión.
     * - Aplica Math.round() para eliminar decimales en la vista.
     */
    const getProgressForTower = (towerNumber: string, activityId: number) => {
        // 1. Encontrar la torre
        const tower = progressTowerData?.towers?.find(t => t.towerNumber === towerNumber);

        // Si no existe la torre en el reporte consolidado
        if (!tower) return "-";

        // 2. Buscar la actividad por ID
        // Nota: Asegúrate que el DTO en el backend y frontend (types) tenga 'idActivity' mapeado correctamente
        const activity = tower.activitis.find((a: any) => a.idActivity === activityId);

        if (activity && activity.percentageProgress !== null && activity.percentageProgress !== undefined) {
            // 3. Redondear siempre al entero más cercano
            const roundedValue = Math.round(activity.percentageProgress);
            return `${roundedValue}%`;
        }

        return "-";
    };

    const getCellStyle = (value: string) => {
        if (value === "-") return {};

        const numericValue = parseInt(value.replace("%", ""), 10);

        // Si es mayor a 0 y menor a 100, pintar amarillo
        if (!isNaN(numericValue) && numericValue > 0 && numericValue < 100) {
            return { backgroundColor: "#FFFACD", color: "#000" };
        }
        return {};
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" marginBottom={5}>Verificación de producción</Typography>

            <Grid container spacing={2} alignItems="center" marginBottom={5}>
               {/* Espacio reservado para filtros */}
            </Grid>

            <TableContainer component={Paper} sx={{ maxHeight: "800px", overflowY: "auto" }}>
                <Table id="tabla-produccion" size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{
                                color: '#004F86',
                                fontWeight: 'bold',
                                borderRight: "1px solid #ccc",
                                position: "sticky",
                                left: 0,
                                top: 0,
                                zIndex: 5,
                                fontSize: "12px",
                                padding: "4px",
                                backgroundColor: "white",
                                minWidth: "80px"
                            }}>Torres</TableCell>

                            {activitiesProject.map((column, index) => (
                                <TableCell
                                    key={column.activity.id || index}
                                    sx={{
                                        fontWeight: "bold",
                                        border: "1px solid #ccc",
                                        textAlign: "center",
                                        fontSize: "12px",
                                        padding: "4px",
                                        backgroundColor: "#78a1bf",
                                        color: "#004F86",
                                        minWidth: "100px"
                                    }}
                                >
                                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                        <span>{column.activity.name}</span>
                                        {column.activity.subActivity && (
                                            <span style={{ fontSize: "0.9em", color: "#ffffff" }}>
                                                {column.activity.subActivity}
                                            </span>
                                        )}
                                    </div>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {towersList.map((row, rowIndex) => (
                            <TableRow key={rowIndex} hover>
                                <TableCell
                                    style={{
                                        position: "sticky",
                                        left: 0,
                                        background: "#fff",
                                        zIndex: 4,
                                        fontWeight: "bold",
                                        borderRight: "1px solid #ccc",
                                        fontSize: "12px",
                                        padding: "4px"
                                    }}
                                    align="center"
                                >
                                    {row.towerNumber}
                                </TableCell>

                                {activitiesProject.map((column, colIndex) => {
                                    // CORRECCIÓN 3: Uso de '|| 0' para evitar error TS si el ID es undefined
                                    const cellValue = getProgressForTower(row.towerNumber, column.activity.id || 0);

                                    return (
                                        <TableCell
                                            key={column.activity.id || colIndex}
                                            align="center"
                                            style={{
                                                fontSize: "12px",
                                                padding: "4px",
                                                borderRight: "1px solid #ccc",
                                                ...getCellStyle(cellValue)
                                            }}
                                        >
                                            {cellValue}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Button
                variant="contained"
                color="primary"
                onClick={exportTableToExcel}
                sx={{ mt: 2 }}
            >
                Descargar Excel
            </Button>
        </Box>
    );
}

export default ProductionVerification;